import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { Fade } from "react-reveal";
import { Link, useNavigate, useParams } from "react-router-dom";
import { catchAsync, checkResponse } from "utilities/utilities";
import {
  ADD_SPECIALITY,
  SPECIALITY_DETAILS,
  UPDATE_SPECIALITY,
  UPLOAD_FILE,
} from "services/ApiCalls";
import Loading from "Component/common/Loading";

// Business Sector Static List
const businessSectors = [
  { name: "Marketing & Media", value: "Marketing & Media" },
  { name: "Film & Series Production", value: "Film & Series Production" },
  { name: "Creative & Design", value: "Creative & Design" },
  { name: "Events & Entertaiment", value: "Events & Entertaiment" },
  {
    name: "Business & Professsional Services",
    value: "Business & Professsional Services",
  },
  { name: "Technology & Development", value: "Technology & Development" },
];

// Validation Schema
const schema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["individual", "company"], {
    required_error: "Type is required",
  }),
  businesSector: z.string().optional(),
  media: z.string().optional(),
});

const AddSpeciality = () => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [preview, setPreview] = useState(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const EditId = !!id;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      type: "individual",
      businesSector: "",
      media: "",
    },
  });

  const typeValue = watch("type");
  const mediaUrl = watch("media");

  useEffect(() => {
    if (mediaUrl) setPreview(mediaUrl);
  }, [mediaUrl]);

  // Upload Handler
  const onMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageUploadLoading(true);
    const formData = new FormData();
    formData.append("files", file);

    try {
      const res = await UPLOAD_FILE(formData);
      if (res?.status === 200) {
        const uploaded = res?.data?.data?.files[0];
        setValue("media", uploaded);
        setPreview(uploaded);
      }
    } finally {
      setImageUploadLoading(false);
    }
  };

  const submitHandler = async (formValues) => {
    setSubmitLoader(true);

    const payload = {
      _id: id,
      name: formValues.name,
      icon: formValues.media,
      type: formValues.type,
      businesSector:
        formValues.type === "company" ? formValues.businesSector : "",
    };

    // If ID exists → EDIT mode → call update API
    const apiToCall = id ? UPDATE_SPECIALITY : ADD_SPECIALITY;

    const res = await apiToCall(payload);

    checkResponse({
      res,
      showSuccess: true,
      navigateUrl: "/speciality",
      navigate,
      setLoader: setSubmitLoader,
    });
  };

  const getData = catchAsync(async () => {
    const res = await SPECIALITY_DETAILS(id);

    checkResponse({
      res,
      setData: (data) => {
        reset({
          name: data.name || "",
          type: data.type || "individual",
          businesSector: data.businesSector || "", // ← FIXED
          media: data.icon || "", // ← FIXED
        });

        if (data.icon) setPreview(data.icon);
      },
    });
  });

  useEffect(() => {
    if (id) getData();
  }, [id]);

  return (
    <Fade>
      <section className="addresources comon_dashpad">
        <form onSubmit={handleSubmit(submitHandler)}>
          <div className="comom_fillter_set text-start">
            <div className="resource_headwrap d-flex align-items-center justify-content-between">
              <h5 className="fillter_head">
                {EditId ? "Edit Speciality" : "Add Speciality"}
              </h5>
            </div>

            {/* Breadcrumb */}
            <div className="breadcrumb_set mt-3">
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/speciality">Manage Speciality</Link>
                </BreadcrumbItem>
                <BreadcrumbItem active>
                  {EditId ? "Edit Speciality" : "Add Speciality"}
                </BreadcrumbItem>
              </Breadcrumb>
            </div>

            {/* Form */}
            <div className="addresources_bottom_form rounded-3 p-3">
              <Row>
                {/* Name */}
                <Col lg={6}>
                  <FormGroup>
                    <Label style={{ color: "white" }}>Name</Label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          {...field}
                          // style={{ color: "black" }}
                        />
                      )}
                    />
                    {errors.name && (
                      <p className="text-danger">{errors.name.message}</p>
                    )}
                  </FormGroup>
                </Col>

                {/* Type Selector */}
                <Col lg={6}>
                  <FormGroup>
                    <Label style={{ color: "white" }}>Type</Label>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className="form-control">
                          <option value="individual">Individual</option>
                          <option value="company">Company</option>
                        </select>
                      )}
                    />
                    {errors.type && (
                      <p className="text-danger">{errors.type.message}</p>
                    )}
                  </FormGroup>
                </Col>

                {/* Business Sector → show only when type = company */}
                {typeValue === "company" && (
                  <Col lg={6}>
                    <FormGroup>
                      <Label style={{ color: "white" }}>Business Sector</Label>
                      <Controller
                        name="businesSector"
                        control={control}
                        render={({ field }) => (
                          <select {...field} className="form-control">
                            <option value="">Select Sector</option>
                            {businessSectors.map((b, i) => (
                              <option key={i} value={b.value}>
                                {b.name}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                    </FormGroup>
                  </Col>
                )}

                {/* Icon Upload */}
                <Col lg={6}>
                  <FormGroup>
                    <Label style={{ color: "white" }}>Icon</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={onMediaUpload}
                    />

                    {imageUploadLoading && <p>Uploading...</p>}

                    {preview && (
                      <div
                        className="mt-2 position-relative"
                        style={{ display: "inline-block" }}
                      >
                        <img
                          src={preview}
                          alt="Preview"
                          style={{ maxWidth: "150px", borderRadius: "8px" }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPreview(null);
                            setValue("media", "");
                          }}
                          className="btn btn-danger position-absolute"
                          style={{ top: -10, right: -10, borderRadius: "50%" }}
                        >
                          X
                        </button>
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {/* Submit */}
              <Col lg={6} className="mt-4">
                <Button className="export_csv w-100" style={{ height: 50 }}>
                  {submitLoader ? <Loading fullSize={true} /> : "Submit"}
                </Button>
              </Col>
            </div>
          </div>
        </form>
      </section>
    </Fade>
  );
};

export default AddSpeciality;
